import type { BudgetProgressView, BudgetTransaction } from "../../entities/ledger/types";

export type CalendarScale = "day" | "week" | "month" | "year";
export interface SpendingBucket { key: string; spent: number; transactions: BudgetTransaction[] }

// UTC is a container for civil-date arithmetic, not the transaction timezone.
export function civilDay(instant: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(instant));
  const part = (name: string) => parts.find((value) => value.type === name)!.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}
export function civilDate(day: string): Date { return new Date(`${day}T00:00:00Z`); }
export function dateKey(date: Date): string { return date.toISOString().slice(0, 10); }
function keyFor(day: string, scale: CalendarScale): string {
  if (scale === "day") return day;
  if (scale === "month") return day.slice(0, 7);
  if (scale === "year") return day.slice(0, 4);
  const date = civilDate(day);
  date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 6) % 7);
  return dateKey(date);
}

export function budgetBuckets(view: BudgetProgressView, scale: CalendarScale, timeZone: string): SpendingBucket[] {
  const start = Date.parse(view.budget.periodStart), end = Date.parse(view.budget.periodEnd);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
  const first = civilDay(view.budget.periodStart, timeZone);
  const last = civilDay(new Date(end - 1).toISOString(), timeZone);
  const grouped = new Map<string, SpendingBucket>();
  const cursor = civilDate(first);
  while (dateKey(cursor) <= last) {
    const key = keyFor(dateKey(cursor), scale);
    if (!grouped.has(key)) grouped.set(key, { key, spent: 0, transactions: [] });
    if (scale === "year") cursor.setUTCFullYear(cursor.getUTCFullYear() + 1, 0, 1);
    else if (scale === "month") cursor.setUTCMonth(cursor.getUTCMonth() + 1, 1);
    else cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  for (const row of view.transactions ?? []) {
    const at = Date.parse(row.effectiveAt);
    if (at < start || at >= end || row.excluded || (row.planned && view.budget.scope !== "general")) continue;
    const bucket = grouped.get(keyFor(civilDay(row.effectiveAt, timeZone), scale));
    if (!bucket) continue;
    bucket.spent += row.bookValue.minor;
    bucket.transactions.push(row);
  }
  return [...grouped.values()];
}
